import { DebouncedFunc } from 'lodash'
import debounce from 'lodash/debounce'
import { isNil } from 'ramda'
import { StateCreator } from 'zustand'

import { reportSentryError } from 'lib/sentry'
import {
  extractTokenFromClientSession,
  SessionAuthenticated,
  SessionUnauthenticated
} from 'modules/customer'
import {
  DeliverySlot,
  TMSDeliverySlotReservationPayload
} from 'modules/delivery'
import { avabilityProductCheck } from 'modules/products'
import {
  apiService,
  createRequestHeadersConfig,
  getQueryClient
} from 'shared/api'
import toast from 'shared/ui/utils/toast'
import { getApiErrors } from 'shared/utils/errors'
import { isNilOrEmpty } from 'shared/utils/ramda'
import { retryPromise } from 'shared/utils/repeatLoop'

import { setPackingOption } from '../api'
import {
  recalculateCartTotals,
  recalculateCartTotalsWithPackingOption
} from '../rules'
import {   ActiveSpecialPromotions,
  AddItemTypes,
  Cart,
  CartItem,
  CartOperation,
  CartReplacementsPayload,
  CartResponseTypes,
PackingOption ,
  ProductsForMerge
} from '../types'

import { processBatchedOperations } from './batching'
import { CartStateActions } from './cart.state'
import { CartState } from './cart.types'
import {
  handleOptimisticAdd,
  handleOptimisticRemove,
  handleOptimisticUpdate
} from './optimistic'

type CartMessage =
  | { type: 'start' }
  | { type: 'stop' }
  | { type: 'cart_updated'; payload: Cart | null }

const api = apiService()

let processBatchedOperationsDebounced: DebouncedFunc<() => void> | null = null
let channel: BroadcastChannel | null = null

const batchIdRef = { current: 0 }
const lastProcessedBatchIdRef = { current: 0 }
const pendingCartOperations = {
  current: {} as { [key: string]: CartOperation }
}
const isProcessingOperations = { current: false }
const waitingExecutionId = { current: 0 }

export const createCartActions: StateCreator<
  CartState,
  [],
  [],
  CartStateActions
> = (set, get) => {
  const updateDerivedData = (cart: Cart | null) => {
    const items = cart?.items || []
    const notAvailable = items?.filter(
      item => !item.isAvailable || !item.isAvailableForDeliverySlot
    )
    const notAvailableInStock = notAvailable?.filter(item => {
      const { isAvailable } = avabilityProductCheck(item.product)

      return !isAvailable
    })
    const productQuantityExceeded = items?.find(item => {
      const { isAvailable, quantityLimit } = avabilityProductCheck(item.product)

      return isAvailable && quantityLimit < item.quantity
    })

    const cartDeliveryMethodId = cart?.deliverySlot?.deliveryMethod?.id
    const notAvailableForDeliveryMethod = cartDeliveryMethodId
      ? items?.filter(item => {
          return !item.product.deliveryMethods.some(
            productDeliveryMethod =>
              productDeliveryMethod.id === cartDeliveryMethodId
          )
        })
      : []

    const promotionsMap = items.reduce(
      (acc, item) => {
        const { promotionId, quantity, valueGross, product } = item

        if (promotionId) {
          if (!acc[promotionId]) {
            acc[promotionId] = {
              promotionId: promotionId,
              quantity: 0,
              valueGross: 0,
              status: 'none',
              activePromotionsQuantity: null
            }
          }
          acc[promotionId].quantity += quantity
          acc[promotionId].valueGross =
            (acc[promotionId].valueGross as number) + valueGross.value
          acc[promotionId].status = 'active'
          acc[promotionId].activePromotionsQuantity = acc[promotionId]
            .activePromotionsQuantity
            ? (acc[promotionId]?.activePromotionsQuantity || 0) + quantity
            : quantity
        } else if (product?.promotions) {
          product.promotions.forEach(promotionItem => {
            const promoId = promotionItem.id

            if (!acc[promoId]) {
              acc[promoId] = {
                promotionId: promoId,
                quantity: 0,
                valueGross: 0,
                status: 'none',
                activePromotionsQuantity: null
              }
            }
            acc[promoId].quantity += quantity
            acc[promoId].valueGross =
              (acc[promoId].valueGross as number) + valueGross.value
            acc[promoId].status = 'pending'
            acc[promoId].activePromotionsQuantity = null
          })
        }

        return acc
      },
      {} as Record<string, ActiveSpecialPromotions>
    )

    set({
      productsNotAvailable: notAvailable,
      productsNotAvailableInStock: notAvailableInStock,
      productsQuantityExceeded: !!productQuantityExceeded,
      productsNotAvailableForDeliveryMethod: notAvailableForDeliveryMethod,
      activeSpecialPromotions: Object.values(promotionsMap)
    })
  }

  return {
    hasPendingOperations: () => {
      return Object.keys(pendingCartOperations.current).length > 0
    },
    flushPendingOperations: async () => {
      if (
        Object.keys(pendingCartOperations.current).length > 0 &&
        processBatchedOperationsDebounced?.flush
      ) {
        channel?.postMessage({ type: 'start' })
        await (processBatchedOperationsDebounced.flush() as
          | Promise<void>
          | undefined)
      }
    },
    // Dodaj cleanup method
    cleanup: () => {
      if (channel) {
        channel.close()
        channel = null
      }

      if (processBatchedOperationsDebounced?.cancel) {
        processBatchedOperationsDebounced.cancel()
      }
      // Wyczyść event listenery
      window.removeEventListener(
        'blur',
        () => void get().flushPendingOperations()
      )
      document.removeEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          void get().flushPendingOperations()
        }
      })
    },
    init: (session: SessionAuthenticated | SessionUnauthenticated) => {
      // Najpierw wyczyść stare połączenia
      get().cleanup()

      channel = new BroadcastChannel('cart_processing_channel')

      const handleMessage = (event: MessageEvent<CartMessage>) => {
        const message = event.data

        if (message.type === 'start') {
          set({ isProcessing: true })
        } else if (message.type === 'stop') {
          set({ isProcessing: false })
        } else if (message.type === 'cart_updated') {
          get().setCart(message.payload)
        }
      }

      channel.addEventListener('message', handleMessage)

      processBatchedOperationsDebounced = debounce(async () => {
        const operations = pendingCartOperations.current
        const operationsCount = Object.keys(operations).length

        if (operationsCount === 0) return

        // Assign a unique ID to this execution
        waitingExecutionId.current += 1
        const currentExecutionId = waitingExecutionId.current

        // Wait for any currently processing operations to complete
        if (isProcessingOperations.current) {
          await retryPromise({
            maxRetries: 40,
            retryDelay: 100,
            check: () =>
              !isProcessingOperations.current ||
              currentExecutionId !== waitingExecutionId.current
          })

          if (currentExecutionId !== waitingExecutionId.current) {
            return
          }
        }

        // Double-check we're still the current execution after waiting

        channel?.postMessage({ type: 'start' })
        isProcessingOperations.current = true

        const operationsCopy = { ...pendingCartOperations.current }

        pendingCartOperations.current = {}
        batchIdRef.current += 1
        const currentBatchId = batchIdRef.current

        const setters = {
          setCart: (cart: Cart | null) => {
            get().setCart(cart)
            channel?.postMessage({ type: 'cart_updated', payload: cart })
          },
          setOptimisticCart: (cart: Cart | null) =>
            set({ optimisticCart: cart }),
          addProductToProcess: get().addProductToProcess,
          removeProductFromProcess: get().removeProductFromProcess,
          trackOperation: get().trackOperation
        }

        void processBatchedOperations(
          operationsCopy,
          get().cart,
          session,
          setters,
          pendingCartOperations.current,
          currentBatchId,
          batchIdRef,
          lastProcessedBatchIdRef
        ).finally(() => {
          setTimeout(() => {
            isProcessingOperations.current = false
            channel?.postMessage({ type: 'stop' })
          }, 1000)
          set({ isLoading: false })
        })
      }, 3000)

      // Dodaj event listenery
      const handleBlur = () => void get().flushPendingOperations()
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'hidden') {
          void get().flushPendingOperations()
        }
      }

      window.addEventListener('blur', handleBlur)
      document.addEventListener('visibilitychange', handleVisibilityChange)
    },

    setCart: (cart: Cart | null) => {
      if (cart && isNil(cart.items)) {
        cart.items = []
      }
      set({ cart, optimisticCart: null })
      updateDerivedData(cart)
    },

    setIsLoading: (isLoading: boolean) => {
      set({ isLoading })
    },

    updateOptimisticCart: (
      updater: (currentCart: Cart | null) => Cart | null
    ) => {
      set(state => {
        const updatedCart = updater(state.optimisticCart || state.cart)

        return {
          optimisticCart: updatedCart
            ? recalculateCartTotals(updatedCart)
            : updatedCart
        }
      })
    },
    reconcileCart: () => {
      if (
        Object.values(get().pendingOperations).every(v => !v) &&
        Object.keys(pendingCartOperations.current).length === 0
      ) {
        set({ optimisticCart: null })
      }
    },
    trackOperation: (operationId: string, isPending: boolean) => {
      set(state => ({
        pendingOperations: {
          ...state.pendingOperations,
          [operationId]: isPending
        }
      }))
      if (!isPending) {
        get().reconcileCart()
      }
    },

    addProductToProcess: (productId: string) => {
      set(state => ({
        productsInProcess: [...state.productsInProcess, productId]
      }))
    },
    removeProductFromProcess: (productId: string) => {
      set(state => ({
        productsInProcess: state.productsInProcess.filter(
          id => id !== productId
        )
      }))
    },

    getProductCount: (productId: string): number => {
      const displayCart = get().optimisticCart || get().cart
      const foundProducts = displayCart
        ? displayCart.items?.filter(i => i.product?.id === productId)
        : null

      return foundProducts
        ? foundProducts.reduce(
            (acc: number, current: CartItem) => acc + current.quantity,
            0
          )
        : 0
    },

    getRestProductsCount: (productId: string, cartItemId: string): number => {
      const displayCart = get().optimisticCart || get().cart
      const foundProducts = displayCart
        ? displayCart.items?.filter(
            i => i.product?.id === productId && i.id !== cartItemId
          )
        : null

      return foundProducts
        ? foundProducts.reduce(
            (acc: number, current: CartItem) => acc + current.quantity,
            0
          )
        : 0
    },

    getAllCartItemsLength: (productId: string): number =>
      get().cart?.items?.filter(item => item.product?.id === productId)
        .length || 0,

    getCartItem: (itemId?: string): CartItem | null => {
      const displayCart = get().optimisticCart || get().cart
      const foundItem = displayCart
        ? displayCart.items?.find(i => i.id === itemId)
        : null

      return foundItem || null
    },

    getCartItemCount: (itemId?: string): number => {
      const displayCart = get().optimisticCart || get().cart
      const foundItem = displayCart
        ? displayCart.items?.find(i => i.id === itemId)
        : null

      return foundItem?.quantity || 0
    },

    getItemByProductId: (productId: string): CartItem | null => {
      const displayCart = get().optimisticCart || get().cart
      const found = displayCart
        ? displayCart.items?.find(i => i.product?.id === productId)
        : null

      return found || null
    },

    getCartById: async (
      id: string,
      token?: string,
      preventOverwriting?: boolean
    ) => {
      if (isNilOrEmpty(id)) return Promise.resolve(null)

      const result: CartResponseTypes = await api.get(
        `/cart/${id}`,
        createRequestHeadersConfig(token)
      )

      if (result) {
        const cartValues = result?.data?.data || null

        if (isNil(cartValues?.items)) {
          cartValues.items = []
        }

        if (!preventOverwriting) {
          get().setCart(cartValues)
        }

        return cartValues
      }

      return null
    },

    createCart: async (
      session: SessionAuthenticated | SessionUnauthenticated
    ) => {
      const token = extractTokenFromClientSession(session)

      try {
        const result: CartResponseTypes = await api.post(
          '/cart',
          {},
          createRequestHeadersConfig(token)
        )

        if (result) {
          const cartValues = result?.data?.data || null

          get().setCart(cartValues)

          return cartValues
        }

        return null
      } catch (err) {
        reportSentryError(err, 'Failed to create cart')

        return null
      }
    },
    mergeCarts: async (
      localCartId: string,
      session: SessionAuthenticated | SessionUnauthenticated
    ) => {
      const parseProductsForCartMerge = (
        givenCart: Cart | null
      ): ProductsForMerge[] => {
        const items = givenCart?.items || []
        // BE splits one product into regular/special/promo lines; update-items sets the total per product
        const byProductId = new Map<string, ProductsForMerge>()

        items.forEach(({ product, quantity }) => {
          const existing = byProductId.get(product.id)

          if (existing) {
            existing.quantity =
              Math.round((existing.quantity + quantity) * 1000) / 1000

            return
          }

          byProductId.set(product.id, {
            productId: product.id,
            name: product.name,
            img:
              product.files[0]?.thumbnailPathMedium ??
              '/static/photo-placeholder.svg',
            quantity
          })
        })

        return [...byProductId.values()]
      }

      try {
        const [authCart, tempCart] = await Promise.all([
          get().createCart(session),
          get()
            .getCartById(localCartId, undefined, true)
            .catch(() => null)
        ])

        return {
          authCartId: authCart?.id,
          authCartDiscountCodeId: authCart?.discountCodes[0]?.id,
          tempCartDiscountCodeText: tempCart?.discountCodes[0]?.code,
          auth: parseProductsForCartMerge(authCart),
          temp: parseProductsForCartMerge(tempCart)
        }
      } catch (err) {
        reportSentryError(err, 'Failed to merge carts')

        return null
      }
    },
    refreshCart: async () => {
      await getQueryClient().refetchQueries({
        queryKey: ['cart', get().cart?.id]
      })

      const cartData = getQueryClient().getQueryData<Cart | null>([
        'cart',
        get().cart?.id
      ])

      return cartData || null
    },

    queueCartOperation: (operation: CartOperation) => {
      processBatchedOperationsDebounced?.cancel()

      const operationKey =
        operation.type === 'update' && operation.itemId
          ? `${operation.type}-${operation.itemId}`
          : `${operation.type}-${operation.productId}`

      pendingCartOperations.current[operationKey] = operation

      if (operation.type === 'add') {
        if (operation.product) {
          get().updateOptimisticCart(currentCart =>
            handleOptimisticAdd(currentCart, [
              {
                productId: operation.productId,
                quantity:
                  typeof operation.visibleQuantity === 'number' &&
                  operation.visibleQuantity >= 0
                    ? operation.visibleQuantity
                    : operation.quantity,
                product: operation.product
              }
            ])
          )
        } else {
          console.warn(
            'Missing product object for optimistic update',
            operation
          )
        }
      } else if (operation.type === 'update' && operation.itemId) {
        get().updateOptimisticCart(currentCart =>
          handleOptimisticUpdate(
            currentCart,
            operation.itemId!,
            operation.visibleQuantity || operation.quantity
          )
        )
      }

      set({ isLoading: true })

      processBatchedOperationsDebounced?.()
    },
    addToCart: async (
      {
        items,
        givenCartId
      }: {
        items: AddItemTypes[]
        givenCartId?: string
      },
      session: SessionAuthenticated | SessionUnauthenticated
    ) => {
      try {
        const operationId = `add-${items[0].productId}-${Date.now()}`

        get().trackOperation(operationId, true)

        if (items.length === 1) {
          const item = items[0]

          get().queueCartOperation({
            type: 'add',
            productId: item.productId,
            quantity: item.quantity,
            product: item.product
          })

          return new Promise<Cart | null>(resolve => {
            setTimeout(() => {
              resolve(get().cart)
            }, 2500)
          })
        } else {
          get().updateOptimisticCart(currentCart =>
            handleOptimisticAdd(currentCart, items)
          )
          get().addProductToProcess(items[0].productId)

          const token = extractTokenFromClientSession(session)
          const id = givenCartId || get().cart?.id || ''
          const result: CartResponseTypes = await api.post(
            `/cart/${id}/add-items`,
            { items },
            createRequestHeadersConfig(token)
          )

          if (result) {
            const cartData = result?.data?.data || null

            if (cartData) {
              get().setCart(cartData)
            }
            get().removeProductFromProcess(items[0].productId)
          }
          get().trackOperation(operationId, false)

          return result?.data?.data || null
        }
      } catch (err) {
        reportSentryError(err, 'Failed to add to cart')

        get().removeProductFromProcess(items[0].productId)
        set({ optimisticCart: null })

        throw err
      }
    },
    removeFromCart: async (
      { itemId }: { itemId: string },
      session: SessionAuthenticated | SessionUnauthenticated
    ) => {
      const token = extractTokenFromClientSession(session)
      const item = get().cart?.items.find(i => i.id === itemId)
      const productId = item?.product.id || ''
      const operationId = `remove-${productId}-${Date.now()}`

      try {
        get().trackOperation(operationId, true)
        get().updateOptimisticCart(currentCart =>
          handleOptimisticRemove(currentCart, itemId)
        )

        if (productId) {
          get().addProductToProcess(productId)
        }

        const result: CartResponseTypes = await api.delete(
          `/cart/${itemId}`,
          createRequestHeadersConfig(token)
        )

        if (result) {
          get().setCart(result?.data?.data || null)
          if (productId) {
            get().removeProductFromProcess(productId)
          }
        }
        get().trackOperation(operationId, false)
      } catch (err) {
        console.error(err)
        set({ optimisticCart: null })
        toast.error(getApiErrors(err))
      }
    },
    clearCart: async (
      session: SessionAuthenticated | SessionUnauthenticated,
      givenCartId?: string
    ) => {
      const token = extractTokenFromClientSession(session)
      const cartId = givenCartId || get().cart?.id || ''

      try {
        set({ isLoading: true })
        const result: CartResponseTypes = await api.delete(
          `/cart/${cartId}/items`,
          createRequestHeadersConfig(token)
        )

        if (result) {
          get().setCart(result?.data?.data || {})
        }
      } catch (err) {
        console.error(err)
      } finally {
        set({ isLoading: false })
      }
    },
    setProductReplacements: (payload: CartReplacementsPayload[]) => {
      if (payload) {
        set({ cartReplacementsPayload: payload })
      }
    },
    getProductReplacements: () => {
      return get().cartReplacementsPayload
    },

    replaceProduct: async (
      items: CartReplacementsPayload[],
      session: SessionAuthenticated | SessionUnauthenticated
    ) => {
      const token = extractTokenFromClientSession(session)
      const cartId = get().cart?.id || ''

      try {
        set({ isLoading: true })
        const result: CartResponseTypes = await api.patch(
          `/cart/${cartId}/replace-product`,
          { items: items },
          createRequestHeadersConfig(token)
        )

        if (result) {
          get().setCart(result?.data?.data || null)

          return result
        }

        return undefined
      } catch (err) {
        console.error(err)

        return undefined
      } finally {
        set({ isLoading: false })
      }
    },

    addSlotToCart: async (
      slot: DeliverySlot | null,
      session: SessionAuthenticated | SessionUnauthenticated
    ) => {
      const currentCart = get().cart

      if (!currentCart) return

      const newCart = {
        ...currentCart,
        deliverySlot: slot,
        deliveryMethodId: slot?.deliveryMethodId,
        deliveryMethod: slot?.deliveryMethod
      }

      get().setCart(newCart)

      try {
        const token = extractTokenFromClientSession(session)
        const cartId = get().cart?.id || ''

        const result: CartResponseTypes = await api.patch(
          `/cart/${cartId}/set-slot/${slot?.id || ''}`,
          {},
          createRequestHeadersConfig(token)
        )

        if (result) {
          get().setCart(result?.data?.data || {})
        } else {
          get().setCart(currentCart)
        }
      } catch (err) {
        get().setCart(currentCart)
        throw err
      }
    },
    TMSReserveSlotToCart: async (
      payload: TMSDeliverySlotReservationPayload,
      session: SessionAuthenticated | SessionUnauthenticated
    ) => {
      const token = extractTokenFromClientSession(session)
      const cartId = get().cart?.id || ''

      try {
        set({ isLoading: true })
        const result: CartResponseTypes = await api.post(
          `/deliveries-tms/slots/reserve-slot/${cartId}`,
          payload,
          {
            ...createRequestHeadersConfig(token),
            transformRequest: [data => JSON.stringify(data)],
            headers: {
              ...createRequestHeadersConfig(token).headers,
              'Content-Type': 'application/json'
            }
          }
        )

        if (result) {
          get().setCart(result?.data?.data || {})
        }
      } catch (err) {
        console.error(err)
      } finally {
        set({ isLoading: false })
      }
    },
    extendSlot: async (
      session: SessionAuthenticated | SessionUnauthenticated
    ) => {
      const token = extractTokenFromClientSession(session)
      const cartId = get().cart?.id || ''

      try {
        const result: CartResponseTypes = await api.patch(
          `/cart/${cartId}/extend-slot`,
          {},
          createRequestHeadersConfig(token)
        )

        if (result) {
          get().setCart(result?.data?.data || {})
        }
      } catch (err) {
        console.error(err)
      } finally {
        set({ isLoading: false })
      }
    },
    addAddressToCart: async (
      addressId: string | null,
      session: SessionAuthenticated | SessionUnauthenticated
    ) => {
      const token = extractTokenFromClientSession(session)
      const cartId = get().cart?.id

      if (cartId) {
        try {
          const result: CartResponseTypes = await api.post(
            `/cart/${cartId}/set-address`,
            { addressId },
            createRequestHeadersConfig(token)
          )

          if (result) {
            const newCart = result?.data?.data || {}

            get().setCart(newCart)

            // Trigger postal code validation if address has a postal code
            // This will be handled by useSyncSelectedDeliveryFromCart (cart/deliverySelection)
            // when cart.deliveryAddress.postalCode changes
          }
        } catch (err) {
          reportSentryError(err, 'Failed to add address to cart')
        } finally {
          set({ isLoading: false })
        }
      } else {
        reportSentryError('Failed to add address to cart: No cart id')
      }
    },
    detachDeliveryOrInvoiceAddressFromCart: async (
      type: 'delivery' | 'invoice',
      session: SessionAuthenticated | SessionUnauthenticated
    ) => {
      const token = extractTokenFromClientSession(session)
      const cartId = get().cart?.id || ''

      try {
        const result: CartResponseTypes = await api.delete(
          `/cart/${cartId}/clear-address/${type}`,
          createRequestHeadersConfig(token)
        )

        if (result) {
          get().setCart(result?.data?.data || {})
        }
      } catch (err) {
        reportSentryError(
          err,
          'Failed to detach delivery or invoice address from cart'
        )
      } finally {
        set({ isLoading: false })
      }
    },
    cancelTmsReservation: async (
      session: SessionAuthenticated | SessionUnauthenticated
    ) => {
      const token = extractTokenFromClientSession(session)
      const cartId = get().cart?.id || ''

      try {
        set({ isLoading: true })
        const result: CartResponseTypes = await api.patch(
          `/cart/${cartId}/detach`,
          {},
          createRequestHeadersConfig(token)
        )

        if (result) {
          get().setCart(result?.data?.data || {})
          toast.success('Rezerwacja została anulowana')
        }
      } catch (err) {
        console.error(err)
      } finally {
        set({ isLoading: false })
      }
    },
    addDiscountCodeToCart: async (
      discountCode: string,
      session: SessionAuthenticated | SessionUnauthenticated,
      cartId?: string
    ) => {
      const token = extractTokenFromClientSession(session)
      const properCartId = cartId || get().cart?.id || ''

      try {
        set({ isLoading: true })
        const result: CartResponseTypes = await api.post(
          `/cart/${properCartId}/add-discount-code`,
          { discountCode },
          createRequestHeadersConfig(token)
        )

        if (result) {
          get().setCart(result?.data?.data || {})
        }
      } finally {
        set({ isLoading: false })
      }
    },
    removeDiscountCodeFromCart: async (
      discountCodeId: string,
      session: SessionAuthenticated | SessionUnauthenticated,
      cartIdParam?: string
    ) => {
      const token = extractTokenFromClientSession(session)
      const cartId = cartIdParam || get().cart?.id || ''

      try {
        const result: CartResponseTypes = await api.delete(
          `/cart/${cartId}/discount-code/${discountCodeId}`,
          createRequestHeadersConfig(token)
        )

        if (result) {
          get().setCart(result?.data?.data || {})
        }
      } finally {
        set({ isLoading: false })
      }
    },
    setPackingOption: async (
      packingOption: PackingOption,
      session: SessionAuthenticated | SessionUnauthenticated
    ) => {
      const currentCart = get().cart

      if (!currentCart) return

      const newCart = recalculateCartTotalsWithPackingOption(
        currentCart,
        packingOption
      )

      get().setCart(newCart)
      const token = extractTokenFromClientSession(session)
      const cartId = get().cart?.id || ''

      try {
        const result = await setPackingOption({
          token,
          cartId,
          packingOptionId: packingOption.id
        })

        if (result) {
          get().setCart(result || {})
        } else {
          get().setCart(currentCart)
        }
      } catch (err) {
        get().setCart(currentCart)
      } finally {
        set({ isLoading: false })
      }
    }
  }
}
